"use client";

import React from "react";
import { Award, CircleCheck, ExternalLink, ShieldCheck, Star } from "lucide-react";
import { Link } from "@/components/common/Link";
import { Button } from "@/components/ui/button";
import { Carousel, CarouselContent, CarouselItem, CarouselNext, CarouselPrevious } from "@/components/ui/carousel";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
const carouselImages = [{
  src: "https://impiantidentali.co/wp-content/uploads/2026/05/WhatsApp-Image-2026-05-17-at-6.30.42-PM-1.jpeg?v=mu3g2e1srh",
  srcset: "https://impiantidentali.co/wp-content/uploads/2026/05/WhatsApp-Image-2026-05-17-at-6.30.42-PM-1.jpeg?v=i481ouflas",
  sizes: "https://impiantidentali.co/wp-content/uploads/2026/05/WhatsApp-Image-2026-05-17-at-6.30.42-PM-1.jpeg?v=xsr8r4r39k",
  alt: "Sala operatoria all'avanguardia presso SkyDental Tirana"
}, {
  src: "https://impiantidentali.co/wp-content/uploads/2026/05/WhatsApp-Image-2026-05-17-at-6.36.43-PM.jpeg?v=gwsrdivuti",
  srcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1663456/img/WhatsApp-Image-2026-05-17-at-6.33.30-PM.png",
  sizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1663456/img/WhatsApp-Image-2026-05-17-at-6.33.30-PM.png",
  alt: "Accogliente area reception della clinica"
}, {
  src: "https://impiantidentali.co/wp-content/uploads/2026/05/consulto_dentale_albania.webp?v=eukcqajebcp",
  srcset: "https://fond-foal.10web.cloud/wp-content/uploads/2026/05/WhatsApp-Image-2026-05-17-at-6.28.26-PM.jpeg?v=b60er0lag6s",
  sizes: "https://fond-foal.10web.cloud/wp-content/uploads/2026/05/WhatsApp-Image-2026-05-17-at-6.28.26-PM.jpeg?v=o50mpz70boo",
  alt: "Tecnologia TAC 3D di ultima generazione"
}];
const certifications = ["Laboratorio odontotecnico interno", "Panoramica (OPG) in clinica", "Sistema implantare dichiarato con codice", "Condizioni di garanzia per iscritto nel piano"];
export default function ClinicaRaccomandataSkyDentalTirana() {
  const {
    ref,
    isVisible
  } = useRevealOnScroll({
    threshold: 0.1
  });
  return <section id="clinica-raccomandata-skydental-tirana" className="bg-background py-20 md:py-28 lg:py-32 overflow-hidden">
      <div className="container mx-auto px-4 md:px-6 max-w-7xl">
        <div ref={ref} className={`transition-all duration-1000 ease-out ${isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"}`}>
          <div className="bg-foreground text-background rounded-3xl overflow-hidden shadow-2xl flex flex-col lg:flex-row border border-border/15">
            {/* Left Carousel Column */}
            <div className="w-full lg:w-2/3 relative bg-card/5 flex flex-col justify-between">
              <div className="py-7 px-6 md:px-8 flex flex-col items-center text-center border-b border-white/10 bg-white/5 backdrop-blur-xs">
                <div className="flex flex-col items-center">
                  <h3 className="font-serif text-2xl md:text-3xl lg:text-4xl text-background font-bold tracking-tight flex items-center gap-3">
                    <Award className="size-7 md:size-8 text-primary shrink-0" />
                    Scelta della Guida 2026
                  </h3>
                  <div className="w-20 h-0.5 bg-primary mt-3 rounded-lg" />
                </div>
              </div>

              <Carousel opts={{
              align: "start",
              loop: true
            }} className="w-full h-full relative">
                <CarouselContent className="h-full -ml-0">
                  {carouselImages.map((image, index) => <CarouselItem key={index} data-index={index} className="pl-0 basis-full h-[420px] sm:h-[500px] lg:h-[620px] relative">
                      <img src={image.src} data-wvc-srcset={image.srcset} data-wvc-sizes={image.sizes} alt={image.alt} className="h-full w-full object-cover" loading={index === 0 ? "eager" : "lazy"} />
                      <div className="absolute inset-0 bg-gradient-to-t from-foreground/80 via-transparent to-transparent pointer-events-none" />
                    </CarouselItem>)}
                </CarouselContent>
                <div className="absolute bottom-6 right-6 md:right-8 flex gap-2.5 z-10">
                  <CarouselPrevious className="relative inset-0 translate-y-0 size-10 rounded-lg bg-background/20 hover:bg-primary hover:text-primary-foreground text-background border border-white/15 backdrop-blur-md transition-colors" />
                  <CarouselNext className="relative inset-0 translate-y-0 size-10 rounded-lg bg-background/20 hover:bg-primary hover:text-primary-foreground text-background border border-white/15 backdrop-blur-md transition-colors" />
                </div>
              </Carousel>
            </div>

            {/* Right Information Column */}
            <div className="w-full lg:w-1/3 p-8 md:p-12 lg:p-14 flex flex-col justify-center relative bg-white/[0.02]">
              <div className="sticky top-12">
                <div className="inline-flex items-center gap-2 px-3 py-1 rounded-lg bg-primary/15 border border-primary/25 text-primary text-xs font-mono font-medium tracking-wide mb-4">
                  <span className="size-2 rounded-lg bg-primary" />
                  Clinica Consigliata
                </div>

                <h2 className="font-serif text-3xl md:text-4xl lg:text-5xl font-bold mb-4 text-background tracking-tight">
                  SkyDental Tirana
                </h2>

                <p className="text-base md:text-lg mb-8 leading-relaxed text-background/80 font-sans">
                  La clinica di riferimento di questa guida: listino per voce, laboratorio odontotecnico interno, panoramica in sede e un referente per i pazienti italiani.
                </p>

                <div className="space-y-4 mb-10 p-5 rounded-2xl bg-white/[0.04] border border-white/10">
                  <h3 className="font-mono text-xs md:text-sm font-semibold text-primary uppercase tracking-wider flex items-center gap-2">
                    <ShieldCheck className="size-4 text-primary shrink-0" />
                    Standard e Certificazioni
                  </h3>
                  <ul className="space-y-3.5">
                    {certifications.map((cert, index) => <li key={index} data-index={index} className="flex items-start gap-3">
                        <CircleCheck className="size-5 text-primary shrink-0 mt-0.5" />
                        <span className="text-sm md:text-base text-background/90 leading-snug">{cert}</span>
                      </li>)}
                  </ul>
                </div>

                <div className="pt-6 border-t border-white/10">
                  <div className="flex items-center gap-2.5 mb-6">
                    <div className="flex text-amber-400">
                      {[...Array(5)].map((_, i) => <Star key={i} data-index={i} className="size-4.5 fill-current" />)}
                    </div>
                    <span className="text-sm font-medium text-background/90 font-mono">
                      4,9/5 su Google (oltre 900 recensioni)
                    </span>
                  </div>

                  <Button asChild size="lg" className="w-full text-base h-14 bg-primary hover:bg-primary/90 text-primary-foreground font-medium rounded-lg shadow-md shadow-primary/20">
                    <Link to="https://skyclinics.al" newTab>
                      Visita il Sito Ufficiale
                      <ExternalLink className="size-5 ml-2" />
                    </Link>
                  </Button>
                  <p className="text-xs text-center text-background/60 mt-4">
                    Link al sito ufficiale di SkyDental Tirana.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>;
}